import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { Location } from 'modules/locations/domain/interfaces/location';
import { deleteLocation } from 'modules/locations/store/actions';
import { ListLocationsArgs, useListLocationsQuery } from 'modules/locations/store/locations-api';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePagedList } from 'shared/hooks/paged-list';
import { AppDispatch } from 'store/store';

/** Ключ localStorage для `perPage`: размер страницы запоминается между визитами. */
const STORAGE_KEY = 'locations';
const DEFAULT_PER_PAGE = 10;

/**
 * Стабильная ссылка обязательна: `usePagedList` считает по аргументам ключ скоупа,
 * а безымянная стрелка меняла бы идентичность на каждом рендере.
 */
const mapSearchToArgs = (search: string): Partial<ListLocationsArgs> => ({ nameFilter: search || undefined });

/**
 * Список локаций организации: страница, размер страницы и поиск живут в URL,
 * данные — в RTK Query.
 *
 * Раньше страница копила локации в redux-слайсе (`fetchLocations` дописывал
 * следующую порцию по `offset: locations.length`, а поиск жил во втором массиве
 * рядом). Теперь запрашивается ровно одна страница, а число страниц выводится
 * приёмом `limit + 1` (см. `usePagedList`).
 */
export const useLocationList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const tournamentId = useSelector(authStoreUserTournamentId);

  const baseArgs = useMemo(() => ({ tournamentId: Number(tournamentId) }), [tournamentId]);

  const list = usePagedList<Location, ListLocationsArgs>({
    useQueryHook: useListLocationsQuery,
    baseArgs,
    mapSearchToArgs,
    storageKey: STORAGE_KEY,
    defaultPerPage: DEFAULT_PER_PAGE,
    // Пока организация неизвестна, запрос ушёл бы за чужими локациями.
    skip: !tournamentId,
    isPending: !tournamentId,
  });

  const removeLocation = useCallback(
    (id: number) => {
      // Мутация осталась танком; список обновит инвалидация тега внутри неё.
      return dispatch(deleteLocation({ id })).unwrap();
    },
    [dispatch],
  );

  return { ...list, removeLocation };
};
